import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as BigCalendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { collection, getDocs, limit, onSnapshot, query, where } from 'firebase/firestore';
import {
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { db } from '../../firebase';
import PageHeader from '../../components/ui/PageHeader';
import FilterChip from '../../components/ui/FilterChip';
import Avatar from '../../components/ui/Avatar';
import { ADMIN_PAGE_WIDTH, cx } from '../../components/ui/utils';
import {
  SCHEDULE_TYPE_STYLES,
  SCHEDULE_STATUS_STYLES,
  getScheduleTypeMeta,
  detectScheduleConflicts,
} from '../../services/scheduleEvents';
import { SCHEDULE_COLLECTION, createScheduleEvent, updateScheduleEvent, deleteScheduleEvent, getScheduleAvailability } from '../../services/scheduleApi';

moment.locale('es');
const localizer = momentLocalizer(moment);

// Mismos 4 roles que Equipo (AdminTeam.js) — con 2 empleados reales, esta
// lista rara vez crece, así que no vale la pena centralizarla en otro lado.
const TEAM_ROLES = ['admin', 'seller', 'services', 'installations', 'demos'];
const ROLE_LABELS = {
  admin: 'Administración',
  seller: 'Ventas',
  services: 'Servicios',
  installations: 'Instalaciones',
  demos: 'Demostraciones',
};

const CALENDAR_MESSAGES = {
  next: 'Sig.',
  previous: 'Ant.',
  today: 'Hoy',
  month: 'Mes',
  week: 'Semana',
  day: 'Día',
  agenda: 'Agenda',
  date: 'Fecha',
  time: 'Hora',
  event: 'Evento',
  noEventsInRange: 'No hay eventos en este rango.',
  showMore: (total) => `+${total} más`,
};

const toLocalInputValue = (date) => {
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const emptyDraft = () => ({
  id: null,
  title: '',
  type: 'meeting',
  status: 'scheduled',
  startAt: new Date(),
  endAt: new Date(Date.now() + 60 * 60000),
  contactKind: 'none', // 'client' | 'lead' | 'none'
  clientId: null,
  leadId: null,
  contactName: '',
  contactEmail: '',
  location: '',
  assignedTo: [],
  notes: '',
});

const CalendarToolbar = ({ label, onNavigate, onView, view }) => (
  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onNavigate('TODAY')}
        className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
      >
        Hoy
      </button>
      <button
        type="button"
        onClick={() => onNavigate('PREV')}
        className="rounded-full border border-slate-200 p-1.5 text-slate-500 transition hover:bg-slate-50"
        aria-label="Anterior"
      >
        <ChevronLeftIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onNavigate('NEXT')}
        className="rounded-full border border-slate-200 p-1.5 text-slate-500 transition hover:bg-slate-50"
        aria-label="Siguiente"
      >
        <ChevronRightIcon className="h-4 w-4" />
      </button>
      <h2 className="ml-2 text-lg font-semibold capitalize text-slate-900">{label}</h2>
    </div>
    <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
      {[
        { id: Views.MONTH, label: 'Mes' },
        { id: Views.WEEK, label: 'Semana' },
        { id: Views.DAY, label: 'Día' },
      ].map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onView(opt.id)}
          className={cx(
            'rounded-lg px-3 py-1.5 text-sm font-semibold transition',
            view === opt.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const CalendarEvent = ({ event }) => {
  const range = event.allDay ? 'Todo el día' : `${moment(event.start).format('h:mm A')}–${moment(event.end).format('h:mm A')}`;
  return (
    <div className="leading-tight" title={`${event.title} · ${range}`}>
      <div className="truncate">{event.title}</div>
      <div className="mt-0.5 text-[0.65rem] font-medium opacity-90">{range}</div>
    </div>
  );
};

const AdminScheduler = () => {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);

  const [view, setView] = useState(Views.MONTH);
  const [date, setDate] = useState(new Date());

  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [contactSearch, setContactSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [syncNotice, setSyncNotice] = useState('');
  const [externalBusy, setExternalBusy] = useState([]);
  const [personalBusyBlocks, setPersonalBusyBlocks] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Agenda en vivo — un solo lugar, todo lo que se crea aquí o desde la
  // pestaña de agenda de un cliente cae en la misma colección.
  useEffect(() => {
    const q = query(collection(db, SCHEDULE_COLLECTION));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            startAt: data.startAt?.toDate ? data.startAt.toDate() : new Date(data.startAt),
            endAt: data.endAt?.toDate ? data.endAt.toDate() : new Date(data.endAt),
          };
        });
        setEvents(list);
        setLoadingEvents(false);
      },
      (err) => {
        console.error('❌ Error cargando agenda:', err);
        setLoadingEvents(false);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadTeam = async () => {
      try {
        const [usersSnap, profilesSnap] = await Promise.all([
          getDocs(query(collection(db, 'users'), where('role', 'in', TEAM_ROLES))),
          getDocs(collection(db, 'teamProfiles')),
        ]);
        const profilesById = Object.fromEntries(profilesSnap.docs.map((docSnap) => [docSnap.id, docSnap.data() || {}]));
        setTeamMembers(usersSnap.docs
          .map((d) => {
            const user = d.data() || {};
            const profile = profilesById[d.id] || {};
            return {
              id: d.id,
              ...user,
              name: profile.displayName || user.name || user.displayName || user.email || 'Equipo',
              imageUrl: profile.imageUrl || user.imageUrl || user.profileImageUrl || '',
            };
          })
          .filter((member) => member.active !== false && member.status !== 'archived'));
      } catch (err) {
        console.error('❌ Error cargando equipo para la agenda:', err);
      }
    };
    const loadClients = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'clients'), limit(200)));
        setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('❌ Error cargando clientes para la agenda:', err);
      }
    };
    const loadLeads = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'leads'), limit(200)));
        setLeads(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('❌ Error cargando leads para la agenda:', err);
      }
    };
    loadTeam();
    loadClients();
    loadLeads();
  }, []);

  // La fecha de renovación ya se administra en el perfil del cliente y es
  // la fuente de verdad. La reflejamos en Agenda sin crear otro documento ni
  // disparar invitaciones de Google Calendar. Si ya existe una cita manual
  // para esa renovación, usamos la cita manual y evitamos el duplicado.
  const hostingRenewalEvents = useMemo(() => clients
    .filter((client) => client.hostingRenewalDate && client.status !== 'deleted')
    .map((client) => {
      const rawDate = client.hostingRenewalDate?.toDate
        ? client.hostingRenewalDate.toDate()
        : new Date(client.hostingRenewalDate);
      if (Number.isNaN(rawDate.getTime())) return null;
      const startAt = moment(rawDate).startOf('day').toDate();
      const endAt = moment(rawDate).add(1, 'day').startOf('day').toDate();
      const clientName = client.businessName || client.name || client.clientName || client.displayName || client.email || 'Cliente sin nombre';
      return {
        id: `hosting_renewal_${client.id}_${moment(startAt).format('YYYY-MM-DD')}`,
        title: `${clientName} — Renovación de hosting`,
        type: 'hosting_renewal',
        status: 'scheduled',
        clientId: client.id,
        startAt,
        endAt,
        allDay: true,
        isHostingRenewal: true,
      };
    })
    .filter(Boolean)
    .filter((renewal) => !events.some((event) => (
      event.type === 'hosting_renewal'
      && event.clientId === renewal.clientId
      && moment(event.startAt).isSame(renewal.startAt, 'day')
    ))), [clients, events]);

  const filteredEvents = useMemo(
    () =>
      [...events, ...hostingRenewalEvents].filter((ev) => {
        if (filterEmployee !== 'all' && !ev.assignedTo?.includes(filterEmployee)) return false;
        if (filterType !== 'all' && ev.type !== filterType) return false;
        if (filterStatus !== 'all' && ev.status !== filterStatus) return false;
        return true;
      }),
    [events, hostingRenewalEvents, filterEmployee, filterType, filterStatus]
  );

  // Solo pedimos free/busy. Nunca se reciben ni se exponen títulos o detalles
  // de las citas personales de Carlo, Yamileth o el resto del equipo.
  const visibleCalendarRange = useMemo(() => {
    const anchor = moment(date);
    if (view === Views.DAY) return { start: anchor.clone().startOf('day'), end: anchor.clone().endOf('day') };
    if (view === Views.WEEK) return { start: anchor.clone().startOf('week'), end: anchor.clone().endOf('week') };
    return {
      start: anchor.clone().startOf('month').startOf('week'),
      end: anchor.clone().endOf('month').endOf('week'),
    };
  }, [date, view]);

  useEffect(() => {
    let active = true;
    if (!teamMembers.length) {
      setPersonalBusyBlocks([]);
      return undefined;
    }

    const loadPersonalBusyBlocks = async () => {
      try {
        const response = await getScheduleAvailability({
          startAt: visibleCalendarRange.start.toDate(),
          endAt: visibleCalendarRange.end.toDate(),
          assigneeIds: teamMembers.map((member) => member.id),
        });
        if (!active) return;
        const blocks = (response.results || []).flatMap((result) => {
          const member = teamMembers.find((item) => item.id === result.userId);
          const memberName = member?.name || member?.email || 'Equipo';
          return (result.busy || []).map((busy) => ({
            id: `personal_busy_${result.userId}_${busy.start}_${busy.end}`,
            title: `Ocupado — ${memberName}`,
            start: new Date(busy.start),
            end: new Date(busy.end),
            isPersonalBusy: true,
            memberId: result.userId,
          }));
        });
        setPersonalBusyBlocks(blocks);
      } catch (err) {
        console.warn('No se pudo cargar disponibilidad personal para la agenda:', err);
        if (active) setPersonalBusyBlocks([]);
      }
    };

    loadPersonalBusyBlocks();
    return () => { active = false; };
  }, [teamMembers, visibleCalendarRange]);

  const visiblePersonalBusyBlocks = useMemo(
    () => personalBusyBlocks.filter((block) => filterEmployee === 'all' || block.memberId === filterEmployee),
    [personalBusyBlocks, filterEmployee]
  );

  const calendarEvents = useMemo(
    () => [
      ...filteredEvents.map((ev) => ({ ...ev, start: ev.startAt, end: ev.endAt })),
      ...visiblePersonalBusyBlocks,
    ],
    [filteredEvents, visiblePersonalBusyBlocks]
  );

  const todayGroupedByEmployee = useMemo(() => {
    const todayStr = new Date().toDateString();
    const todays = events
      .filter((ev) => ev.status !== 'cancelled' && ev.startAt?.toDateString() === todayStr)
      .sort((a, b) => a.startAt - b.startAt);

    const groups = teamMembers.map((member) => ({
      member,
      items: todays.filter((ev) => ev.assignedTo?.includes(member.id)),
    }));
    const unassigned = todays.filter((ev) => !ev.assignedTo?.length);
    return { groups, unassigned };
  }, [events, teamMembers]);

  const calendarEventPropGetter = useCallback((event) => {
    if (event.isPersonalBusy) {
      return {
        style: {
          backgroundColor: '#64748b',
          backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,.16) 0, rgba(255,255,255,.16) 6px, transparent 6px, transparent 12px)',
          border: '1px solid #475569',
          borderRadius: '8px',
        },
        className: 'text-xs font-semibold',
      };
    }
    const meta = getScheduleTypeMeta(event.type);
    const cancelled = event.status === 'cancelled';
    return {
      style: {
        backgroundColor: meta.hex,
        opacity: cancelled ? 0.45 : 1,
        border: 'none',
        borderRadius: '8px',
      },
      className: cx('text-xs font-semibold', cancelled && 'line-through'),
    };
  }, []);

  const openCreateModal = useCallback((slot) => {
    setError('');
    setSyncNotice('');
    setContactSearch('');
    setConfirmDelete(false);
    const start = slot?.start || new Date();
    const end = slot?.end && slot.end > start ? slot.end : new Date(start.getTime() + 60 * 60000);
    setDraft({ ...emptyDraft(), startAt: start, endAt: end });
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((event) => {
    setError('');
    setContactSearch('');
    setConfirmDelete(false);
    setDraft({
      id: event.id,
      title: event.title || '',
      type: event.type || 'meeting',
      status: event.status || 'scheduled',
      startAt: event.startAt,
      endAt: event.endAt,
      contactKind: event.clientId ? 'client' : event.leadId ? 'lead' : 'none',
      clientId: event.clientId || null,
      leadId: event.leadId || null,
      contactName: event.contactName || '',
      contactEmail: event.contactEmail || '',
      location: event.location || '',
      assignedTo: event.assignedTo || [],
      notes: event.notes || '',
    });
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setDraft(emptyDraft());
    setError('');
  };

  const contactMatches = useMemo(() => {
    const needle = contactSearch.trim().toLowerCase();
    if (!needle) return [];
    const clientMatches = clients
      .filter((c) => [c.businessName, c.name, c.email].filter(Boolean).some((v) => v.toLowerCase().includes(needle)))
      .slice(0, 6)
      .map((c) => ({ kind: 'client', id: c.id, label: c.businessName || c.name || c.email, email: c.email || '' }));
    const leadMatches = leads
      .filter((l) => [l.name, l.email].filter(Boolean).some((v) => v.toLowerCase().includes(needle)))
      .slice(0, 6)
      .map((l) => ({ kind: 'lead', id: l.id, label: l.name || l.email, email: l.email || '' }));
    return [...clientMatches, ...leadMatches];
  }, [contactSearch, clients, leads]);

  const selectContact = (match) => {
    setDraft((prev) => ({
      ...prev,
      contactKind: match.kind,
      clientId: match.kind === 'client' ? match.id : null,
      leadId: match.kind === 'lead' ? match.id : null,
      contactName: match.label,
      contactEmail: match.email,
    }));
    setContactSearch('');
  };

  const clearContact = () => {
    setDraft((prev) => ({ ...prev, contactKind: 'none', clientId: null, leadId: null, contactName: '', contactEmail: '' }));
  };

  const toggleAssignee = (uid) => {
    setDraft((prev) => ({
      ...prev,
      assignedTo: prev.assignedTo.includes(uid)
        ? prev.assignedTo.filter((id) => id !== uid)
        : [...prev.assignedTo, uid],
    }));
  };

  const conflicts = useMemo(
    () => detectScheduleConflicts(draft, events, { excludeId: draft.id }),
    [draft, events]
  );

  useEffect(() => {
    let active = true;
    if (!modalOpen || !draft.assignedTo.length || !draft.startAt || !draft.endAt || draft.endAt <= draft.startAt) {
      setExternalBusy([]);
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      try {
        const response = await getScheduleAvailability({
          startAt: draft.startAt,
          endAt: draft.endAt,
          assigneeIds: draft.assignedTo,
        });
        if (active) setExternalBusy((response.results || []).filter((item) => item.busy?.length));
      } catch (err) {
        console.warn('No se pudo consultar disponibilidad personal:', err);
        if (active) setExternalBusy([]);
      }
    }, 350);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [modalOpen, draft.assignedTo, draft.startAt, draft.endAt]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) {
      setError('Añade un título para el evento.');
      return;
    }
    if (draft.endAt <= draft.startAt) {
      setError('La hora de fin debe ser después de la hora de inicio.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        title: draft.title.trim(),
        type: draft.type,
        status: draft.status,
        startAt: draft.startAt,
        endAt: draft.endAt,
        clientId: draft.clientId,
        leadId: draft.leadId,
        contactName: draft.contactName || null,
        contactEmail: draft.contactEmail || null,
        location: draft.location.trim(),
        assignedTo: draft.assignedTo,
        notes: draft.notes.trim(),
      };
      let result;
      if (draft.id) {
        if (draft.status === 'cancelled') {
          result = await deleteScheduleEvent(draft.id);
        } else {
          result = await updateScheduleEvent(draft.id, payload);
        }
      } else {
        result = await createScheduleEvent(payload);
      }
      if (result?.syncStatus === 'failed') setSyncNotice(result.warning || 'La cita quedó guardada, pero Google Calendar requiere revisión.');
      closeModal();
    } catch (err) {
      console.error('❌ Error guardando evento de agenda:', err);
      setError('No se pudo guardar el evento. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft.id) return;
    setSaving(true);
    try {
      const result = await deleteScheduleEvent(draft.id);
      if (result?.syncStatus === 'failed') setSyncNotice(result.warning || 'La cita quedó cancelada, pero Google Calendar requiere revisión.');
      closeModal();
    } catch (err) {
      console.error('❌ Error eliminando evento de agenda:', err);
      setError('No se pudo eliminar el evento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className={cx('mx-auto px-4 py-6 sm:px-6 lg:px-8', ADMIN_PAGE_WIDTH)}>
        <PageHeader
          title="Agenda"
          description="Llamadas, demos, instalaciones y soporte — todo lo que 2 personas manejan por el equipo entero."
          actions={
            <button
              type="button"
              onClick={() => openCreateModal(null)}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              <PlusIcon className="h-4 w-4" />
              Nueva cita
            </button>
          }
        />

        {syncNotice && (
          <div className="mt-4 flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <span>{syncNotice}</span>
            <button type="button" onClick={() => setSyncNotice('')} className="font-semibold hover:underline">Entendido</button>
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <FilterChip label="Todos" active={filterEmployee === 'all'} onClick={() => setFilterEmployee('all')} />
          {teamMembers.map((m) => (
            <FilterChip
              key={m.id}
              label={m.name || m.email}
              active={filterEmployee === m.id}
              onClick={() => setFilterEmployee(m.id)}
            />
          ))}
          <span className="mx-1 h-5 w-px bg-slate-200" />
          <FilterChip label="Todo tipo" active={filterType === 'all'} onClick={() => setFilterType('all')} />
          {Object.entries(SCHEDULE_TYPE_STYLES).map(([key, meta]) => (
            <FilterChip key={key} label={meta.label} active={filterType === key} onClick={() => setFilterType(key)} />
          ))}
          <span className="mx-1 h-5 w-px bg-slate-200" />
          <FilterChip label="Todo estado" active={filterStatus === 'all'} onClick={() => setFilterStatus('all')} />
          {Object.entries(SCHEDULE_STATUS_STYLES).map(([key, meta]) => (
            <FilterChip key={key} label={meta.label} active={filterStatus === key} onClick={() => setFilterStatus(key)} />
          ))}
        </div>

        <div className="mt-5 flex flex-col gap-5 lg:flex-row">
          <div className="flex-1 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
            <BigCalendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              views={[Views.MONTH, Views.WEEK, Views.DAY]}
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              selectable
              onSelectSlot={openCreateModal}
              onSelectEvent={(event) => {
                if (!event.isPersonalBusy && !event.isHostingRenewal) openEditModal(event);
              }}
              showAllEvents
              eventPropGetter={calendarEventPropGetter}
              messages={CALENDAR_MESSAGES}
              culture="es"
              style={{ height: 680 }}
              components={{ toolbar: CalendarToolbar, event: CalendarEvent }}
            />
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm bg-slate-500" />
                Ocupado — calendario personal (sin detalles)
              </span>
              <span>Las citas de BBTS conservan el color de su tipo.</span>
            </div>
            {loadingEvents && <p className="mt-3 text-center text-sm text-slate-400">Cargando agenda...</p>}
          </div>

          <div className="w-full flex-shrink-0 space-y-4 lg:w-80">
            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500">
                <UserGroupIcon className="h-4 w-4" />
                Hoy por persona
              </h3>
              <div className="mt-3 space-y-3">
                {todayGroupedByEmployee.groups.map(({ member, items }) => (
                  <div key={member.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                    <div className="flex items-center gap-2">
                      <Avatar src={member.imageUrl} name={member.name || member.email} size="sm" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-800">{member.name || member.email}</p>
                        <p className="text-xs text-slate-400">{ROLE_LABELS[member.role] || member.role}</p>
                      </div>
                    </div>
                    {items.length === 0 ? (
                      <p className="mt-2 text-xs text-slate-400">Sin citas hoy.</p>
                    ) : (
                      <ul className="mt-2 space-y-1.5">
                        {items.map((ev) => (
                          <li key={ev.id}>
                            <button
                              type="button"
                              onClick={() => openEditModal(ev)}
                              className="flex w-full items-center justify-between gap-2 rounded-lg bg-white px-2.5 py-1.5 text-left text-xs shadow-sm hover:ring-1 hover:ring-slate-200"
                            >
                              <span className="truncate font-medium text-slate-700">{ev.title}</span>
                              <span className="flex-shrink-0 text-slate-400">{moment(ev.startAt).format('h:mm A')}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {todayGroupedByEmployee.unassigned.length > 0 && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-700">
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      Sin asignar
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {todayGroupedByEmployee.unassigned.map((ev) => (
                        <li key={ev.id}>
                          <button
                            type="button"
                            onClick={() => openEditModal(ev)}
                            className="flex w-full items-center justify-between gap-2 rounded-lg bg-white px-2.5 py-1.5 text-left text-xs shadow-sm hover:ring-1 hover:ring-amber-200"
                          >
                            <span className="truncate font-medium text-slate-700">{ev.title}</span>
                            <span className="flex-shrink-0 text-slate-400">{moment(ev.startAt).format('h:mm A')}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {todayGroupedByEmployee.groups.every((g) => g.items.length === 0) &&
                  todayGroupedByEmployee.unassigned.length === 0 && (
                    <p className="text-sm text-slate-400">No hay citas para hoy.</p>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">{draft.id ? 'Editar cita' : 'Nueva cita'}</h2>
              <button type="button" onClick={closeModal} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100" aria-label="Cerrar">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 px-6 py-5">
              <div>
                <label className="block text-sm font-medium text-slate-700">Título</label>
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Demo de ERP restaurante, llamada de seguimiento..."
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-slate-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Tipo</label>
                  <select
                    value={draft.type}
                    onChange={(e) => setDraft((prev) => ({ ...prev, type: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-slate-500"
                  >
                    {Object.entries(SCHEDULE_TYPE_STYLES).map(([key, meta]) => (
                      <option key={key} value={key}>{meta.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Estado</label>
                  <select
                    value={draft.status}
                    onChange={(e) => setDraft((prev) => ({ ...prev, status: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-slate-500"
                  >
                    {Object.entries(SCHEDULE_STATUS_STYLES).map(([key, meta]) => (
                      <option key={key} value={key}>{meta.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Inicio</label>
                  <input
                    type="datetime-local"
                    value={toLocalInputValue(draft.startAt)}
                    onChange={(e) => setDraft((prev) => ({ ...prev, startAt: new Date(e.target.value) }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Fin</label>
                  <input
                    type="datetime-local"
                    value={toLocalInputValue(draft.endAt)}
                    onChange={(e) => setDraft((prev) => ({ ...prev, endAt: new Date(e.target.value) }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-slate-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Cliente o lead (opcional)</label>
                {draft.contactName ? (
                  <div className="mt-1 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                    <span>
                      <span className={cx('mr-2 rounded-full px-2 py-0.5 text-xs font-semibold', draft.contactKind === 'client' ? 'bg-violet-100 text-violet-700' : 'bg-orange-100 text-orange-700')}>
                        {draft.contactKind === 'client' ? 'Cliente' : 'Lead'}
                      </span>
                      {draft.contactName}
                    </span>
                    <button type="button" onClick={clearContact} className="text-xs font-semibold text-slate-400 hover:text-slate-600">
                      Quitar
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="search"
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Buscar cliente o lead por nombre/correo"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-slate-500"
                    />
                    {contactMatches.length > 0 && (
                      <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                        {contactMatches.map((match) => (
                          <li key={`${match.kind}_${match.id}`}>
                            <button
                              type="button"
                              onClick={() => selectContact(match)}
                              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                            >
                              <span className="truncate">{match.label}</span>
                              <span className={cx('flex-shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold', match.kind === 'client' ? 'bg-violet-100 text-violet-700' : 'bg-orange-100 text-orange-700')}>
                                {match.kind === 'client' ? 'Cliente' : 'Lead'}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Ubicación</label>
                <input
                  type="text"
                  value={draft.location}
                  onChange={(e) => setDraft((prev) => ({ ...prev, location: e.target.value }))}
                  placeholder="Virtual (Google Meet), oficina del cliente, oficina BBTS..."
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Asignar a</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {teamMembers.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => toggleAssignee(m.id)}
                      className={cx(
                        'rounded-full border px-3 py-1.5 text-xs font-semibold transition',
                        draft.assignedTo.includes(m.id)
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      )}
                    >
                      <Avatar src={m.imageUrl} name={m.name || m.email} size="sm" className="-ml-1" />
                      {m.name || m.email}
                    </button>
                  ))}
                  {teamMembers.length === 0 && <p className="text-xs text-slate-400">No hay miembros de equipo registrados en Equipo.</p>}
                </div>
              </div>

              {conflicts.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>
                    Choca con: {conflicts.map((c) => `${c.title} (${moment(c.startAt).format('h:mm A')})`).join(', ')}
                  </span>
                </div>
              )}

              {externalBusy.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>
                    Hay un compromiso personal marcado como ocupado para: {externalBusy.map((item) => teamMembers.find((member) => member.id === item.userId)?.name || 'miembro asignado').join(', ')}. BBTS no muestra sus detalles.
                  </span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700">Notas</label>
                <textarea
                  rows={3}
                  value={draft.notes}
                  onChange={(e) => setDraft((prev) => ({ ...prev, notes: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-slate-500"
                />
              </div>

              {error && <p className="text-sm text-rose-600">{error}</p>}

              <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                {draft.id ? (
                  confirmDelete ? (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-500">¿Eliminar esta cita?</span>
                      <button type="button" onClick={handleDelete} className="font-semibold text-rose-600 hover:underline">Sí, eliminar</button>
                      <button type="button" onClick={() => setConfirmDelete(false)} className="font-semibold text-slate-400 hover:underline">Cancelar</button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(true)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 hover:text-rose-700"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Eliminar
                    </button>
                  )
                ) : (
                  <span />
                )}
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                >
                  {saving ? 'Guardando...' : draft.id ? 'Guardar cambios' : 'Crear cita'}
                </button>
              </div>

              {draft.contactEmail && (
                <p className="text-xs text-slate-400">
                  Se enviará confirmación a {draft.contactEmail} y se agregará a Google Calendar.
                </p>
              )}
              {draft.clientId && (
                <Link to={`/admin/clients/${draft.clientId}/profile`} className="block text-xs font-medium text-sky-600 hover:underline">
                  Abrir perfil del cliente
                </Link>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminScheduler;
